import React, { Suspense, useEffect, useRef } from 'react';
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useAuth, type AuthUser } from '../../contexts/AuthContext';
import Layout from '../../components/Layout';
import LoadingFallback from './LoadingFallback';
import { SharedRoutes } from './sharedRoutes';
import { TherapistRoutes } from './therapistRoutes';
import { ClientRoutes } from './clientRoutes';
import { AdminRoutes } from './adminRoutes';
import { userHasRole, isClientUser, landingFor } from './helpers';
import lazyWithRetry from '../../utils/lazyWithRetry';
import {
  ConsentGateRoutes,
  TermsOfServicePage,
  PrivacyPolicyPage,
  BusinessAssociateAgreementPage,
  SignupConsentPage,
} from './ConsentGateRoutes';

const SubscriptionLockoutPage = lazyWithRetry(() => import('../../pages/SubscriptionLockoutPage'));
const AdminSettingsLazy = lazyWithRetry(() => import('../../features/admin-settings/AdminSettings'));
const FeesPage = lazyWithRetry(() => import('../../pages/FeesPage'));

/** Tiny route component that triggers logout on mount. */
function LogoutRoute() {
  const { logout } = useAuth();
  useEffect(() => { logout('/login'); }, [logout]);
  return <div>Logging out…</div>;
}

const BILLING_BLOCKED_STATUSES = new Set(['past_due', 'suspended']);

/**
 * AppRouter - Main routing configuration
 * 
 * Composes all route modules:
 * - SharedRoutes: Auth, public, and profile routes
 * - TherapistRoutes: Provider-facing routes
 * - ClientRoutes: Client portal routes
 * - AdminRoutes: Administrator routes
 * 
 * When the org subscription is past_due or suspended:
 * - Non-admin users see a full lockout page
 * - Admin-access users can reach admin settings only
 */
export function AppRouter() {
  const { user } = useAuth();

  const isBillingBlocked =
    user &&
    typeof user === 'object' &&
    BILLING_BLOCKED_STATUSES.has(user.subscription_status ?? '');

  const hasAdminAccess =
    user && typeof user === 'object' && user.has_admin_settings_access;

  // Consent gate: authenticated non-demo users who haven't completed consent
  // are redirected to the standalone consent page.
  const needsConsent =
    user &&
    typeof user === 'object' &&
    !user.demo_mode &&
    user.signup_consent_completed === false;

  if (needsConsent) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 flex flex-col">
        <Suspense fallback={<LoadingFallback />}>
          <ConsentGateRoutes />
        </Suspense>
      </div>
    );
  }

  // Full lockout for non-admin users when billing is blocked
  if (isBillingBlocked && !hasAdminAccess) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 flex flex-col">
        <Suspense fallback={<LoadingFallback />}>
          <SubscriptionLockoutPage />
        </Suspense>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 flex flex-col">
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          {/* Public legal pages — rendered without Layout (no sidebar/header) */}
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/business-associate-agreement" element={<BusinessAssociateAgreementPage />} />
          <Route path="/baa" element={<Navigate to="/business-associate-agreement" replace />} />
          <Route path="/fees" element={<FeesPage />} />
          <Route path="/fee" element={<Navigate to="/fees" replace />} />

          {/* Signup consent page — rendered without Layout (standalone step) */}
          <Route path="/signup-consent" element={<SignupConsentPage />} />

          {/* Logout — rendered without Layout so it doesn't create a tab */}
          <Route path="/logout" element={<LogoutRoute />} />

          {/* All other routes use the full app Layout */}
          <Route path="*" element={
            <LayoutWrappedRoutes user={user} isBillingBlocked={isBillingBlocked} />
          } />
        </Routes>
      </Suspense>
    </div>
  );
}

/**
 * Lightweight wrapper that plays a CSS fade-in when the top-level
 * pathname changes.  Uses page-transition-enter / page-transition-enter-active
 * classes defined in index.css.  Respects prefers-reduced-motion.
 */
function PageTransition({ children, locationKey }: { children: React.ReactNode; locationKey: string }) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const prevKeyRef = useRef(locationKey);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el || locationKey === prevKeyRef.current) return;
    prevKeyRef.current = locationKey;

    // Apply enter class, then activate on next frame
    el.classList.add('page-transition-enter');
    const rafId = requestAnimationFrame(() => {
      el.classList.add('page-transition-enter-active');
      el.classList.remove('page-transition-enter');
    });

    // Guard against bubbling transitionend from child elements — only
    // react when the wrapper div itself finishes its transition.
    const onEnd = (e?: TransitionEvent) => {
      if (e && e.target !== el) return;
      el.classList.remove('page-transition-enter-active');
      el.removeEventListener('transitionend', onEnd as EventListener);
    };
    el.addEventListener('transitionend', onEnd as EventListener);
    // Fallback cleanup in case transitionend doesn't fire
    const fallback = setTimeout(onEnd, 200);
    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(fallback);
      el.removeEventListener('transitionend', onEnd as EventListener);
      el.classList.remove('page-transition-enter', 'page-transition-enter-active');
    };
  }, [locationKey]);

  return <div ref={wrapperRef} className="h-full min-h-0">{children}</div>;
}

function LayoutWrappedRoutes({ user, isBillingBlocked }: { user: AuthUser | false | null; isBillingBlocked: boolean }) {
  const location = useLocation();
  const isCalendarPage = location.pathname === '/dashboard' || location.pathname === '/admin/dashboard' || location.pathname === '/calendar' || location.pathname.startsWith('/calendar/');
  const isAssistantPage = location.pathname === '/ai-assistant';
  const isCaseWorkspace = location.pathname.startsWith('/cases/');
  const pageOverflowClass = isCaseWorkspace
    ? 'overflow-visible p-0'
    : `${isAssistantPage ? 'overflow-hidden' : 'overflow-y-auto'} p-4 sm:p-6`;

  // Use only the top-level path segment as the transition key so that
  // navigating within a section (e.g. /clients/1 → /clients/2) doesn't flash.
  const transitionKey = '/' + (location.pathname.split('/')[1] || '');

  return (
    <Layout user={user}>
      <div className={isCalendarPage ? '' : `flex-1 min-h-0 ${pageOverflowClass} bg-gray-50 dark:bg-gray-900`}>
        <Suspense fallback={<LoadingFallback />}>
          <PageTransition locationKey={transitionKey}>
          <Routes>
            {SharedRoutes({ user })}
            {isBillingBlocked ? (
              <>
                {/* Admin-access users: only admin settings + subscription lockout */}
                <Route path="/admin/settings" element={
                  <React.Suspense fallback={<LoadingFallback />}>
                    <AdminSettingsLazy />
                  </React.Suspense>
                } />
                <Route path="*" element={
                  <React.Suspense fallback={<LoadingFallback />}>
                    <SubscriptionLockoutPage />
                  </React.Suspense>
                } />
              </>
            ) : (
              <>
                {TherapistRoutes({ user, userHasRole, isClientUser })}
                {ClientRoutes({ user, isClientUser })}
                {AdminRoutes({ user, userHasRole, isClientUser })}
                {/* Catch-all: redirect unmatched paths to the user's landing page */}
                <Route path="*" element={
                  user ? <Navigate to={landingFor(user)} replace /> : <Navigate to="/login" replace />
                } />
              </>
            )}
          </Routes>
          </PageTransition>
        </Suspense>
      </div>
    </Layout>
  );
}

// Re-export helpers and the consent-gate component for backward compatibility
export { ConsentGateRoutes } from './ConsentGateRoutes';
export { userHasRole, isClientUser, landingFor } from './helpers';
